import { useState, useEffect, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { toast } from "react-toastify";
import { subscribeToAvailableUsers, subscribeToAdmins, addAdmin, removeAdmin } from "../services/adminUserService";

export function useAdminUser(onUserSelected) {
    const currentUserEmail = getAuth().currentUser?.email;
    const saneEmail = currentUserEmail ? currentUserEmail.replace(/\./g, "-") : null;

    const [availableUsers, setAvailableUsers] = useState([]);
    const [authUser, setAuthUser] = useState(currentUserEmail || "");
    const [loading, setLoading] = useState(true);

    const [showManageAdmins, setShowManageAdmins] = useState(false);
    const [adminsList, setAdminsList] = useState([]);
    const [newAdminEmail, setNewAdminEmail] = useState("");

    useEffect(() => {
        return subscribeToAvailableUsers(
            (userIDs) => {
                // Ensure current admin is always in list
                if (saneEmail && !userIDs.includes(saneEmail)) {
                    userIDs.unshift(saneEmail);
                }
                setAvailableUsers(userIDs);
                setLoading(false);
            },
            (error) => {
                console.error("Error fetching active users:", error);
                if (saneEmail) {
                    setAvailableUsers([saneEmail]);
                }
                setLoading(false);
            }
        );
    }, [saneEmail]);

    useEffect(() => {
        if (!showManageAdmins) return;
        return subscribeToAdmins((list) => setAdminsList(list));
    }, [showManageAdmins]);

    const handleSelectUser = useCallback(() => {
        const sanitized = authUser ? authUser.replace(/\./g, "-") : "";
        if (sanitized && availableUsers.includes(sanitized)) {
            onUserSelected(sanitized);
        } else {
            toast.error("Please select a valid user from the list.");
        }
    }, [authUser, availableUsers, onUserSelected]);

    const handleAddAdmin = async () => {
        if (!newAdminEmail) return;
        if (!newAdminEmail.includes("@")) {
            toast.error("Invalid email address.");
            return;
        }
        
        const sanitized = newAdminEmail.replace(/\./g, "-");
        try {
            await addAdmin(sanitized);
            setNewAdminEmail("");
            toast.success("Admin added successfully.");
        } catch (e) {
            toast.error(`Error adding admin: ${e.message}`);
        }
    };

    const handleRemoveAdmin = async (key) => {
        if (key === saneEmail) {
            toast.error("You cannot remove yourself from the admin list!");
            return;
        }

        if (!window.confirm(`Are you sure you want to remove admin access for ${key}?`)) {
            return;
        }

        try {
            await removeAdmin(key);
            toast.success("Admin removed successfully.");
        } catch (e) {
            toast.error(`Error removing admin: ${e.message}`);
        }
    };

    return {
        loading,
        availableUsers,
        authUser,
        setAuthUser,
        handleSelectUser,
        
        showManageAdmins,
        setShowManageAdmins,
        adminsList,
        newAdminEmail,
        setNewAdminEmail,
        handleAddAdmin,
        handleRemoveAdmin,
        saneEmail
    };
}
